import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api } from "../../lib/api";
import { handleError } from "../../lib/errors";
import { notify } from "../../lib/notify";
import { useAppStore } from "../../store/useAppStore";
import { Project } from "../../types";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

// Mirrors NewProjectDialog's name/description rules. Only these two fields are
// editable (the update API accepts name + description); environments are managed
// separately via the environment selector.
const editProjectSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Project name must be at least 2 characters." })
    .max(50, { message: "Project name must not exceed 50 characters." }),
  description: z
    .string()
    .max(200, { message: "Description must not exceed 200 characters." })
    .optional(),
});

type EditProjectFormData = z.infer<typeof editProjectSchema>;

interface EditProjectDialogProps {
  project: Project;
  /** Trigger element. Defaults to nothing — caller usually passes a Button. */
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Called after a successful save (e.g. to refetch the project). */
  onProjectUpdated?: () => void;
}

export function EditProjectDialog({
  project,
  children,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  onProjectUpdated,
}: EditProjectDialogProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const { updateProject } = useAppStore();

  // Support both controlled and uncontrolled open state (like NewProjectDialog).
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;

  const form = useForm<EditProjectFormData>({
    resolver: zodResolver(editProjectSchema),
    defaultValues: {
      name: project.name,
      description: project.description ?? "",
    },
  });

  // Re-seed the form from the latest project whenever the dialog opens, so a
  // cancelled edit (or an externally-changed project) doesn't show stale values.
  React.useEffect(() => {
    if (open) {
      form.reset({
        name: project.name,
        description: project.description ?? "",
      });
    }
    // form is stable; we only want to reset on open / project identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project.id, project.name, project.description]);

  const onSubmit = async (data: EditProjectFormData) => {
    try {
      const result = await api.projects.update(project.id, {
        name: data.name,
        description: data.description || "",
      });

      // Keep the store's projects list in sync so /projects reflects the edit
      // immediately, then let the caller refetch the detail view.
      updateProject(project.id, result.project ?? data);
      notify.success("Project updated");
      setOpen(false);
      onProjectUpdated?.();
    } catch (error) {
      handleError(error, {
        scope: "EditProjectDialog",
        context: { id: project.id },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit project</DialogTitle>
          <DialogDescription>
            Update your project's name and description.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Name</FormLabel>
                  <FormControl>
                    <Input placeholder="my-awesome-app" {...field} />
                  </FormControl>
                  <FormDescription>
                    This will be used as your project identifier.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="A brief description of your project"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Brief description of what your project does.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
